/**
 * ByteSpike DosiaBundle wire parser — 单一真相源（红线#9）。
 *
 * 网关 bundle 端点（企业 `/api/v1/dosia-org/bundle` 与个人版
 * `/api/v1/me/dosia-bundle`，PR-DOSIA-PERSONAL-BUNDLE 2026-07）返回同一
 * snake_case wire shape（网关侧同一 buildLiveBundle builder 保证）。本模块是
 * 客户端侧唯一的 wire → camelCase 转换点：
 *   - 企业链：DOSIA backend（apps/web/server bytespike-client）代员工拉取后转换再 WS 广播
 *   - 个人链：desktop 主进程（bytespike-personal-runtime）直连网关拉取后转换
 * 两个消费者都 import 这里，禁止各自手搓第二份字段映射（漂移=消费端崩）。
 *
 * 语义解析边界：`capabilities.defaults` 保持 raw 透传（唯一 parse 点 =
 * @dosia/core parseModelDefaults），本模块只做形状搬运不做语义。
 *
 * 2026-07 从 apps/web/server/enterprise/bytespike-client.ts 原样提升（红线#8
 * 替换式迁移，server 侧只留 HTTP client 壳）。
 */
import { THINKING_LEVELS } from "./types/index.js";
import { warnPiWireRowsMissingTranslated } from "./pi-wire.js";
const THINKING_LEVEL_SET = new Set(THINKING_LEVELS);
const MAX_OUTPUT_TOKEN_PARAM = 1_048_576;
function isObj(x) {
    return x != null && typeof x === "object" && !Array.isArray(x);
}
/**
 * 网关 `output_token_param`（输出上限的请求字段名 + 默认 + 单次上限）的唯一解析点——公开清单
 *（core manifest-parser，模型级）与 bundle（本文件 mapBundleCapabilities，`capabilities` 下）共用。
 * 2026-10-08 从 core manifest-parser 提升到这里（N-121 批次 B）：bundle 路径此前整段丢弃这个字段。
 * 畸形 / 缺 field → undefined；max 超出上界或 default 超过 max 的分量丢弃。
 */
export function parseOutputTokenParam(raw) {
    if (!isObj(raw) || typeof raw.field !== "string" || raw.field.trim().length === 0)
        return undefined;
    const positiveInteger = (value) => typeof value === "number" && Number.isInteger(value) && value >= 1 ? value : undefined;
    const rawMax = positiveInteger(raw.max);
    const max = rawMax !== undefined && rawMax <= MAX_OUTPUT_TOKEN_PARAM ? rawMax : undefined;
    const candidateDefault = positiveInteger(raw.default);
    const defaultValue = candidateDefault !== undefined && (max === undefined || candidateDefault <= max)
        ? candidateDefault
        : undefined;
    return {
        field: raw.field.trim(),
        ...(defaultValue !== undefined ? { default: defaultValue } : {}),
        ...(max !== undefined ? { max } : {}),
    };
}
/**
 * 网关协议行 `reasoning_replay` → 两个已知取值之一；其它值（缺席 / 脏值 / 未来新增）= 未知 = undefined。
 * 公开清单腿与 bundle 腿共用。
 */
export function mapWireReasoningReplay(raw) {
    return raw === "passthrough" || raw === "as_text" ? raw : undefined;
}
/**
 * 多个分组的同一 (model, protocol) 行合并成一行时的逐面兼容提示——公开清单解析（core manifest-parser）与
 * desktop bundle 去重（aggregateBundleToGroups）共用这一个函数。保守：
 *  - `acceptsDeveloperRole`：任一组明说 false → false；否则任一组缺席 → 未知；全部 true 才 true
 *    （某组的账号不保证时，合并后的这一行就不能对外说「保证」）。
 *  - `reasoningReplay`：各组一致才保留，不一致或任一缺席 → 未知（网关自己对「各账号结论不一致」也是省略）。
 * 产物两个键总在（值可为 undefined），调用方 `Object.assign` 覆盖旧值即可。
 */
export function mergeFaceCompat(left, right) {
    const l = left.acceptsDeveloperRole;
    const r = right.acceptsDeveloperRole;
    const acceptsDeveloperRole = l === false || r === false
        ? false
        : l === undefined || r === undefined ? undefined : true;
    const reasoningReplay = left.reasoningReplay === right.reasoningReplay ? left.reasoningReplay : undefined;
    return { acceptsDeveloperRole, reasoningReplay };
}
/**
 * Parse the gateway M1 structured `thinking_param` descriptor — single source
 * (moved here from core/enterprise/manifest-parser.ts 2026-07-17, 红线#9): both
 * the public-manifest ingestion (core/manifest-parser.ts, imports this from
 * @dosia/shared) AND this file's own bundle-wire ingestion (mapBundleCapabilities
 * below) need it. It must live in @dosia/shared — core can import shared but not
 * the reverse (依赖方向 shared ← core), and bytespike-bundle-wire.ts (this file)
 * is itself in shared, so it cannot import from core.
 *
 * Null-safe: malformed / absent (pre-M1) → undefined, so the resolver falls back
 * to the boolean capability trio. Levels filtered to the known ThinkingLevel
 * vocab. Tolerates both the documented snake `can_disable` wire form AND a
 * defensive camelCase `canDisable` (bundle may pass through camel directly);
 * snake wins if both are present.
 */
export function parseThinkingParamDescriptor(raw) {
    if (!isObj(raw))
        return undefined;
    const levels = Array.isArray(raw.levels)
        ? raw.levels.filter((x) => typeof x === "string" && THINKING_LEVEL_SET.has(x))
        : [];
    const dflt = typeof raw.default === "string" && THINKING_LEVEL_SET.has(raw.default) ? raw.default : undefined;
    const tp = { supported: raw.supported === true };
    if (typeof raw.wire === "string")
        tp.wire = raw.wire;
    if (levels.length)
        tp.levels = levels;
    if (dflt)
        tp.default = dflt;
    const canDisable = raw.can_disable ?? raw.canDisable;
    if (typeof canDisable === "boolean")
        tp.canDisable = canDisable;
    if (typeof raw.source === "string")
        tp.source = raw.source;
    return tp;
}
/**
 * 网关 `tier_defaults` 的唯一解析点（红线 #7：解析接口输入先运行时 guard）。
 *
 * **缺失、非对象、三档全空/全空白 → 一律 `undefined`**，即「网关没有答案，保留你自己的兜底」。
 * 三者绝不可被读成「清空档位」：空档位会让 Agent SDK 落回内建的 haiku 家族 id，在非 Claude
 * 组上 404 —— 正是本字段存在的理由。网关侧只在派生出非空三元组时才下发（`IsZero()` 时 omit），
 * 所以「缺失」与「空对象」在生产里同义；此处显式把两条路径都归一，不依赖那个巧合。
 *
 * 只收非空 string，逐档 trim；部分档为空是合法的（该组没有能回答那一档的模型），保留其余档。
 */
export function parseGatewayTierDefaults(raw) {
    if (!isObj(raw))
        return undefined;
    const out = {};
    for (const tier of ["opus", "sonnet", "haiku"]) {
        const v = raw[tier];
        if (typeof v === "string" && v.trim())
            out[tier] = v.trim();
    }
    return out.opus || out.sonnet || out.haiku ? out : undefined;
}
/**
 * bundle 行的一句话简介 → 清单行 `description`（N-152）。bundle 只带网关目录的**中文**那一版（英文版只在公开清单
 * 与资料表里），所以记为 `{ zh }`；非字符串 / 全空白 → 缺席。bundle → 清单行的三处转换（桌面
 * `aggregateBundleToGroups`、企业后端 `persistServerModelCatalog`、云端执行机 core `catalogFromWireModels`）
 * 都经这里，别处不另判 bundle 简介是哪种语言。
 */
export function bundleDescriptionText(raw) {
    return typeof raw === "string" && raw.trim() ? { zh: raw } : undefined;
}
/** Wire `string[]` → 过滤后的 string[]。非数组 = 未知 = undefined（不得 `?? []` 喂给 for...of 以外的「缺席」语义）。 */
export function mapWireStringList(raw) {
    if (!Array.isArray(raw))
        return undefined;
    return raw.filter((x) => typeof x === "string");
}
/**
 * Wire bool → `boolean | undefined`。非 boolean（缺席 / 脏值）= 未知。
 * 共识原则 ③：缺席不得回落成 false；运行时准入一律 `=== true`。
 * 公开清单腿与 bundle 腿共用，禁止第二条手搓路径。
 */
export function mapWireOptionalBool(raw) {
    return typeof raw === "boolean" ? raw : undefined;
}
/**
 * 网关 `document_input` 的唯一解析点。`scanned_pdf` 只在 boolean 时写入——缺席保持
 * undefined（共识原则 ③：缺字段 = 未知 = 不进池，不得回落成 false）。
 */
export function mapWireDocumentInput(raw) {
    if (!isObj(raw))
        return undefined;
    const formats = mapWireStringList(raw.formats);
    const scannedRaw = raw.scanned_pdf ?? raw.scannedPdf;
    const scannedPdf = typeof scannedRaw === "boolean" ? scannedRaw : undefined;
    const maxPagesRaw = raw.max_pages ?? raw.maxPages;
    const maxFileMbRaw = raw.max_file_mb ?? raw.maxFileMb;
    const notes = typeof raw.notes === "string" && raw.notes ? raw.notes : undefined;
    const source = typeof raw.source === "string" && raw.source ? raw.source : undefined;
    const out = {};
    if (formats)
        out.formats = formats;
    if (scannedPdf !== undefined)
        out.scannedPdf = scannedPdf;
    if (typeof maxPagesRaw === "number")
        out.maxPages = maxPagesRaw;
    if (typeof maxFileMbRaw === "number")
        out.maxFileMb = maxFileMbRaw;
    if (notes)
        out.notes = notes;
    if (source)
        out.source = source;
    return Object.keys(out).length ? out : undefined;
}
/**
 * 公开清单 `modalities` 的 ALL 一半。**不**解析 `*_partial` / `*_unsupported`。
 * bundle 腿没有此嵌套字段——不要在 toBundle 里调用本函数去硬造。
 */
export function mapWireModalities(raw) {
    if (!isObj(raw))
        return undefined;
    const input = mapWireStringList(raw.input);
    const output = mapWireStringList(raw.output);
    if (!input && !output)
        return undefined;
    return {
        ...(input ? { input } : {}),
        ...(output ? { output } : {}),
    };
}
export function mapBundleCapabilities(c, 
// model-entry-level fallback: W2 places param_schema/unsupported_params at the
// model level (PR-2a); accept either location, model nested-in-caps wins if both.
model) {
    const unsupportedParams = c?.unsupported_params ?? model?.unsupported_params;
    const paramSchema = c?.param_schema ?? model?.param_schema;
    if (!c && !unsupportedParams && !paramSchema)
        return undefined;
    return {
        supportsVision: c?.supports_vision,
        supportsVideo: c?.supports_video,
        supportsImageGeneration: mapWireOptionalBool(c?.supports_image_generation),
        supportsImageEditing: mapWireOptionalBool(c?.supports_image_editing),
        supportsVideoGeneration: mapWireOptionalBool(c?.supports_video_generation),
        supportsVideoEditing: mapWireOptionalBool(c?.supports_video_editing),
        supportsTextPrompt: mapWireOptionalBool(c?.supports_text_prompt),
        supportsAudio: c?.supports_audio,
        supportsFile: c?.supports_file,
        supportsToolUse: c?.supports_tool_use,
        supportsThinking: c?.supports_thinking,
        supports1MContext: c?.supports_1m_context,
        supportsEffort: c?.supports_effort,
        supportedEffortLevels: c?.supported_effort_levels,
        thinkingParam: parseThinkingParamDescriptor(c?.thinking_param),
        supportsAutoMode: c?.supports_auto_mode,
        contextWindow: c?.context_window,
        effectiveContextWindow: c?.effective_context_window,
        outputLimit: c?.output_limit,
        maxTokens: c?.max_tokens,
        outputTokenParam: parseOutputTokenParam(c?.output_token_param),
        unsupportedParams,
        paramSchema,
        defaults: c?.defaults,
    };
}
function mapShadowedGroup(g) {
    const out = { id: g.id, name: g.name };
    if (typeof g.reason === "string")
        out.reason = g.reason;
    return out;
}
function mapGroupRef(g) {
    return {
        id: g.id,
        name: g.name,
        rateMultiplier: g.rate_multiplier,
        imageRateIndependent: g.image_rate_independent,
        imageRateMultiplier: g.image_rate_multiplier,
        // P2-I：仅显式 "pi" 透传（防御性消费）；缺省/"anthropic"/未知值 → undefined = Claude 路径。
        backendProtocol: g.backend_protocol === "pi" ? "pi" : undefined,
    };
}
function mapPricing(p) {
    if (!p)
        return undefined;
    return {
        billingMode: p.billing_mode,
        inputPer1m: p.input_per_1m,
        outputPer1m: p.output_per_1m,
        cacheReadPer1m: p.cache_read_per_1m,
        cacheWritePer1m: p.cache_write_per_1m,
        imageOutputPer1m: p.image_output_per_1m,
        perImage: p.per_image,
    };
}
/** 网关漏发 `translated` 的告警前缀（固定串，便于在员工机 / 服务端日志里检索）。 */
export const ANTHROPIC_LEG_MISSING_TRANSLATED_LOG = "[model-catalog] anthropic-leg-missing-translated";
const lastMissingTranslatedSignature = new Map();
/**
 * 网关下发的行里是 `anthropic_messages`、却没带 `translated` 布尔的 model id（去重、保序），并在非空时打一条
 * 固定前缀的告警。网关契约是每条协议行都下发这个布尔、刻意不 omitempty（llm-gateway
 * `DosiaBundleModelEntry.Translated` / `CatalogProtocol.Translated`），所以非空 = 网关漏发或旧网关：这些行
 * 按 `isNativeAnthropicLeg` 判不了、**不进 Claude 主脑池**（fail-closed），员工端表现为模型在选主脑面
 * 消失——这条日志让它能被发现。解析入口（bundle `toBundle` / 公开清单 `parseByteSpikeManifest`）各调一次；
 * 同一来源缺失集合不变时不重复打（解析在会话热路径上会被反复调用）。
 */
export function warnAnthropicRowsMissingTranslated(rows, source) {
    const ids = [...new Set(rows
            .filter((row) => row.protocol === "anthropic_messages" && typeof row.translated !== "boolean")
            .map((row) => row.modelId))];
    const signature = ids.join(",");
    if (ids.length > 0 && lastMissingTranslatedSignature.get(source) !== signature) {
        console.warn(`${ANTHROPIC_LEG_MISSING_TRANSLATED_LOG} source=${source} count=${ids.length} models=${signature}`
            + " — gateway omitted `translated`; these anthropic legs are treated as not native and left out of the Claude brain pool");
    }
    lastMissingTranslatedSignature.set(source, signature);
    return ids;
}
export function toBundle(data) {
    const payload = {
        pushVersion: data.push_version,
        pushedAt: data.pushed_at,
        pushedByAccount: data.pushed_by_account,
        orgCodeLabel: data.org_code_label,
        entitledGroups: data.entitled_groups,
        models: (data.models ?? []).map((m) => ({
            modelId: m.model_id,
            protocol: m.protocol,
            baseUrl: m.base_url,
            apiKey: m.api_key,
            vendor: m.vendor,
            family: m.family,
            ...(typeof m.family_slug === "string" && m.family_slug.trim()
                ? { familySlug: m.family_slug }
                : {}),
            chosenGroup: mapGroupRef(m.chosen_group),
            availableGroups: (m.available_groups ?? []).map(mapGroupRef),
            ...(typeof m.available === "boolean" ? { available: m.available } : {}),
            ...(typeof m.translated === "boolean" ? { translated: m.translated } : {}),
            ...(typeof m.unavailable_reason === "string" && m.unavailable_reason
                ? { unavailableReason: m.unavailable_reason }
                : {}),
            ...(Array.isArray(m.shadowed_groups) && m.shadowed_groups.length > 0
                ? { shadowedGroups: m.shadowed_groups.map(mapShadowedGroup) }
                : {}),
            pricing: mapPricing(m.pricing),
            // 网关 `tier_defaults` → 按组名收口的 map（而非扁平单值）。理由是下游去重的形状：
            // bundle 的行键是 (model_id, protocol, **group_id**)，同一模型被多个组供给时会出多行、
            // 各带各组的三元组；而 DOSIA 客户端 catalog（desktop aggregateBundleToGroups）按
            // (modelId, protocol) 去重，扁平单值会让 group_id 更大的那些组的三元组静默消失，员工
            // 正好在那个组时就拿不到档位 → 落回派生 → 回到 404 那条路。map 让去重点做并集而非丢弃。
            ...(() => {
                const td = parseGatewayTierDefaults(m.tier_defaults);
                const g = m.chosen_group?.name;
                return td && g ? { tierDefaultsByGroup: { [g]: td } } : {};
            })(),
            completeUrl: m.complete_url,
            endpointPath: m.endpoint_path,
            capabilities: mapBundleCapabilities(m.capabilities, m),
            displayName: m.display_name,
            description: m.description,
            capabilityTags: m.capability_tags,
            docsUrl: m.docs_url,
            // document_input 只读 capabilities（prod106：行级 0/38）；server_tools /
            // carries_document_input 仍在模型行级。嵌套 modalities 只在公开清单腿，这里不硬造。
            ...(() => {
                const documentInput = mapWireDocumentInput(m.capabilities?.document_input);
                const serverTools = mapWireStringList(m.server_tools);
                const serverToolsPartial = mapWireStringList(m.server_tools_partial);
                const acceptsDeveloperRole = mapWireOptionalBool(m.accepts_developer_role);
                const reasoningReplay = mapWireReasoningReplay(m.reasoning_replay);
                return {
                    ...(documentInput ? { documentInput } : {}),
                    ...(serverTools ? { serverTools } : {}),
                    ...(serverToolsPartial ? { serverToolsPartial } : {}),
                    ...(typeof m.carries_document_input === "boolean"
                        ? { carriesDocumentInput: m.carries_document_input }
                        : {}),
                    ...(acceptsDeveloperRole !== undefined ? { acceptsDeveloperRole } : {}),
                    ...(reasoningReplay ? { reasoningReplay } : {}),
                };
            })(),
        })),
    };
    warnAnthropicRowsMissingTranslated(payload.models, "bundle");
    warnPiWireRowsMissingTranslated(payload.models, "bundle");
    return payload;
}
//# sourceMappingURL=bytespike-bundle-wire.js.map